/**
 * GroupPostsFeed — renders group posts with the same card and overlays as the
 * other web feeds, but wired to GroupContext so likes/saves/comments mutate the
 * group lists instead of the main feed.
 */
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import feedService from '@wlb/shared/api/services/feedService';
import profileService from '@wlb/shared/api/services/profileService';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { GroupFeedPost } from '@wlb/shared/types/context';
import type { FeedPost } from '@wlb/shared/types/post';
import { PostCard } from '../feed/PostCard';
import { PostListSkeleton } from '../feed/PostListSkeleton';
import { CommentsModal } from '../feed/CommentsModal';
import { LikesModal } from '../feed/LikesModal';
import { PostOptionsModal } from '../feed/PostOptionsModal';
import { ReportPostModal } from '../feed/ReportPostModal';
import { SharePostModal } from '../feed/SharePostModal';
import { ConfirmModal } from '../feed/ConfirmModal';
import { BlockUserModal } from '../feed/BlockUserModal';
import { RobiInfoModal } from '../feed/RobiInfoModal';
import { usePostBuddySuggest } from '../feed/usePostBuddySuggest';
import { AiBuddyDisclaimerModal } from '../settings/AiBuddyDisclaimerModal';
import { ListStatus } from '../../components/ui/ListStatus';
import { resolvePostImageUri } from '../feed/postMedia';
import { editPostRouteState } from '../createPost/createPostRoute';
import { useRestoreScrollOnReturn } from '../createPost/useRestoreScrollOnReturn';
import { getPageScrollY } from '../../utils/pageScrollLock';
import type { PostOptionKey } from '../feed/postOptions';
import { useCurrentProfile } from '../profile/useCurrentProfile';
import { useOpenUserProfile } from '../profile/useOpenUserProfile';
import { useOpenDirectConversation } from '../messages/useOpenDirectConversation';
import '../../components/ui/skeleton.css';
import '../feed/feed.css';

interface GroupPostsFeedProps {
  posts: GroupFeedPost[];
  loading: boolean;
  emptyMessage: string;
  error?: string | null;
  /**
   * True once a request answered. Without it a failed first load cannot be told
   * apart from an empty group, so the empty copy would replace the failure.
   */
  loaded?: boolean;
  onRetry?: () => void;
}

export function GroupPostsFeed({
  posts,
  loading,
  emptyMessage,
  error,
  loaded = true,
  onRetry,
}: GroupPostsFeedProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useCurrentProfile();
  const openUserProfile = useOpenUserProfile();
  const { openConversation } = useOpenDirectConversation();
  const {
    likeGroupPost,
    saveGroupPost,
    deleteGroupPost,
    updateGroupCommentCount,
    toggleGroupBlockUser,
  } = useGroup();

  const [commentPost, setCommentPost] = useState<FeedPost | null>(null);
  const [likesPost, setLikesPost] = useState<FeedPost | null>(null);
  const [mediaPost, setMediaPost] = useState<FeedPost | null>(null);
  const [mediaIndex, setMediaIndex] = useState(0);
  // Stable so PostCard's memo holds — see PostCard.
  const handleMediaClick = useCallback((post: FeedPost, index?: number) => {
    setMediaPost(post);
    setMediaIndex(index ?? 0);
  }, []);
  const [menuPost, setMenuPost] = useState<FeedPost | null>(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [reportPost, setReportPost] = useState<FeedPost | null>(null);
  const [sharePostTarget, setSharePostTarget] = useState<FeedPost | null>(null);
  const [deleteTargetPost, setDeleteTargetPost] = useState<FeedPost | null>(null);
  const [blockTargetPost, setBlockTargetPost] = useState<FeedPost | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);

  const postBuddy = usePostBuddySuggest({ updateCommentCount: updateGroupCommentCount });

  // Editing a group post leaves for /create and comes back, so the list needs the
  // same restore as the home feed and the profile list.
  useRestoreScrollOnReturn(posts.length > 0);

  const closeMenu = () => {
    setMenuPost(null);
    setMenuAnchorRect(null);
  };

  const handleCardMenu = useCallback((target: FeedPost, anchorRect: DOMRect) => {
    setMenuPost(target);
    setMenuAnchorRect(anchorRect);
  }, []);

  const handleLike = useCallback(
    (postId: string) => {
      likeGroupPost(postId).catch(() => undefined);
    },
    [likeGroupPost],
  );

  const handleMenuSelect = async (action: PostOptionKey) => {
    if (!menuPost) return;
    const currentPost = menuPost;
    closeMenu();

    switch (action) {
      case 'save':
        saveGroupPost(currentPost.id).catch(() => undefined);
        break;
      case 'message':
        // Resolves by numeric user id, so the existing thread with this member
        // is reused and only a first-time chat hits Conversation/Initiate.
        await openConversation({
          userId: currentPost.userId ?? 0,
          name: currentPost.username,
          avatar: typeof currentPost.avatar === 'string' ? currentPost.avatar : null,
        });
        break;
      case 'profile':
        openUserProfile(currentPost.profileUserGuid);
        break;
      case 'report':
        setReportPost(currentPost);
        break;
      case 'block':
        setBlockTargetPost(currentPost);
        break;
      case 'edit':
        navigate('/create', {
          state: editPostRouteState(currentPost, location, getPageScrollY()),
        });
        break;
      case 'delete':
        setDeleteTargetPost(currentPost);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetPost) return;
    const target = deleteTargetPost;
    setDeleteTargetPost(null);
    const result = await feedService.deletePost(target.id);
    if (result.success) {
      deleteGroupPost(target.id);
      return;
    }
    ToastService.show({ type: 'error', message: t('common.errors.generic') });
  };

  const handleConfirmBlock = async () => {
    if (!blockTargetPost) return;
    const targetUserId = blockTargetPost.userId;
    const username = blockTargetPost.username;
    if (targetUserId == null) {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
      return;
    }
    setBlockLoading(true);
    const shouldBlock = !blockTargetPost.isBlocked;
    const result = await profileService.setBlockRelation(targetUserId, shouldBlock);
    setBlockLoading(false);
    if (result.success) {
      toggleGroupBlockUser(targetUserId, username, shouldBlock);
      setBlockTargetPost(null);
      ToastService.show({
        type: 'success',
        message: shouldBlock
          ? t('home.postOptions.blockSuccessMessage', { username })
          : t('home.postOptions.unblockSuccessMessage', { username }),
      });
      return;
    }
    ToastService.show({ type: 'error', message: result.message || t('common.errors.generic') });
  };

  const renderList = () => {
    if (loading && posts.length === 0) {
      return <PostListSkeleton />;
    }
    // Reported as a failure whether or not a retry callback was supplied —
    // previously a missing `onRetry` silently degraded this into "no posts".
    if (error && posts.length === 0) {
      return <ListStatus variant="error" message={error} onAction={onRetry} busy={loading} />;
    }
    if (!loaded && posts.length === 0) {
      return <PostListSkeleton />;
    }
    if (posts.length === 0) {
      return (
        <ListStatus
          variant="empty"
          title={emptyMessage}
          onAction={onRetry}
          busy={loading}
        />
      );
    }
    return (
      <div className="feed-list skeleton-fade-in">
        {posts.map(post => (
          <PostCard
            key={post.id}
            post={post}
            onLike={handleLike}
            onComment={setCommentPost}
            onShare={setSharePostTarget}
            onLikes={setLikesPost}
            onMediaClick={handleMediaClick}
            onMenu={handleCardMenu}
            onRobiPress={postBuddy.handleRobiPress}
            robiLoadingPostId={postBuddy.robiLoadingPostId}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {renderList()}
      {commentPost && (
        <CommentsModal
          post={commentPost}
          initialView="comments"
          onClose={() => setCommentPost(null)}
          onCommentAdded={() => updateGroupCommentCount(commentPost.id, 1)}
          onCommentCountChange={delta => updateGroupCommentCount(commentPost.id, delta)}
          onMenu={(target, anchorRect) => { setMenuPost(target); setMenuAnchorRect(anchorRect); }}
        />
      )}
      {likesPost && <LikesModal postId={likesPost.id} onClose={() => setLikesPost(null)} />}
      {mediaPost && (
        <CommentsModal
          post={mediaPost}
          fullView
          mediaViewer
          initialMediaIndex={mediaIndex}
          onClose={() => setMediaPost(null)}
          onCommentAdded={() => updateGroupCommentCount(mediaPost.id, 1)}
          onCommentCountChange={delta => updateGroupCommentCount(mediaPost.id, delta)}
          onMenu={(target, anchorRect) => { setMenuPost(target); setMenuAnchorRect(anchorRect); }}
        />
      )}
      <PostOptionsModal
        post={menuPost}
        profile={profile}
        anchorRect={menuAnchorRect}
        onClose={closeMenu}
        onSelect={handleMenuSelect}
      />
      {reportPost && <ReportPostModal post={reportPost} onClose={() => setReportPost(null)} />}
      {sharePostTarget && (
        <SharePostModal post={sharePostTarget} onClose={() => setSharePostTarget(null)} />
      )}
      {deleteTargetPost && (
        <ConfirmModal
          title={t('home.postOptions.deleteConfirmTitle')}
          message={t('home.postOptions.deleteConfirmMessage')}
          confirmLabel={t('home.postOptions.deleteConfirm')}
          cancelLabel={t('home.postOptions.deleteConfirmCancel')}
          mediaUrl={resolvePostImageUri(deleteTargetPost.image)}
          onClose={() => setDeleteTargetPost(null)}
          onConfirm={() => { handleConfirmDelete().catch(() => undefined); }}
          showIcon
        />
      )}
      {blockTargetPost && (
        <BlockUserModal
          username={blockTargetPost.username}
          avatarUrl={typeof blockTargetPost.avatar === 'string' ? blockTargetPost.avatar : null}
          seed={blockTargetPost.profileUserGuid ?? blockTargetPost.userId}
          isBlocked={blockTargetPost.isBlocked}
          loading={blockLoading}
          onClose={() => setBlockTargetPost(null)}
          onConfirm={() => { handleConfirmBlock().catch(() => undefined); }}
        />
      )}
      {postBuddy.disclaimerPost && (
        <AiBuddyDisclaimerModal
          onClose={() => postBuddy.setDisclaimerPost(null)}
          onContinue={() => { postBuddy.handleDisclaimerContinue().catch(() => undefined); }}
        />
      )}
      {postBuddy.robiInfoOpen && (
        <RobiInfoModal onClose={() => postBuddy.setRobiInfoOpen(false)} />
      )}
    </>
  );
}
